import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API_ENDPOINTS from '../config';
import { authenticatedFetch, authenticatedFetchJson } from '../utils/api.js';

export default function PaymentConfirmation() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentProof, setPaymentProof] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      const data = await authenticatedFetchJson(`${API_ENDPOINTS.INVOICE_USER_BY_ID}/${id}`);
      setInvoice(data.invoice);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError(t('paymentConfirmation.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleConfirmPayment = async () => {
    if (!paymentProof.trim()) {
      window.alert(t('paymentConfirmation.enterPaymentProof'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.INVOICE_CONFIRM_PAYMENT}/${id}/confirm-payment`, {
        method: 'POST',
        body: JSON.stringify({ paymentProof })
      });

      if (!response.ok) {
        throw new Error('Failed to confirm payment');
      }

      const data = await response.json();
      window.alert(data.message);
      navigate('/invoice');
    } catch (error) {
      console.error('Error confirming payment:', error);
      window.alert(t('paymentConfirmation.errorConfirming'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '18px', color: '#6b7280' }}>{t('paymentConfirmation.loadingInvoice')}</div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
        <div style={{ fontSize: '18px', color: '#ef4444' }}>{error || t('paymentConfirmation.invoiceNotFound')}</div>
        <button
          onClick={() => navigate('/invoice')}
          style={{
            marginTop: '1rem',
            padding: '8px 16px',
            background: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          {t('paymentConfirmation.backToInvoices')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <button
            onClick={() => navigate('/invoice')}
            style={{
              background: '#f1f5f9',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#374151',
              marginBottom: '1rem',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e2e8f0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f1f5f9'}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7z"/>
            </svg>
            {t('paymentConfirmation.backToInvoices')}
          </button>
          
          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1f2937', margin: '0 0 0.5rem 0' }}>
            {t('paymentConfirmation.title')}
          </h1>
          <p style={{ color: '#6b7280', margin: 0 }}>
            {t('paymentConfirmation.confirmPaymentFor', { invoiceNumber: invoice.invoiceNumber })}
          </p>
        </div>

        {/* Invoice Details */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', marginBottom: '1.5rem' }}>
            {t('paymentConfirmation.invoiceDetails')}
          </h2>
          
          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <div>
              <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.invoiceNumber')}:</strong> {invoice.invoiceNumber}
              </p>
              <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
                <strong>{t('paymentConfirmation.referenceNumber')}:</strong> {invoice.referenceNumber}
              </p>
              <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.amount')}:</strong> {invoice.amount} {invoice.currency}
              </p>
              <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.description')}:</strong> {invoice.description}
              </p>
            </div>
            
            <div>
              <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.dueDate')}:</strong> {formatDate(invoice.dueDate)}
              </p>
              <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.status')}:</strong> 
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'white',
                  background: '#f59e0b',
                  marginLeft: '8px'
                }}>
                  {t('paymentConfirmation.waitingForPayment')}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Payment Instructions */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', marginBottom: '1.5rem' }}>
            {t('paymentConfirmation.paymentDetails')}
          </h2>
          
          <div style={{
            background: '#f0f9ff',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid #0ea5e9'
          }}>
            <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
              <strong>{t('paymentConfirmation.bank')}:</strong> {invoice.bankName}
            </p>
            <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
              <strong>{t('paymentConfirmation.accountNumber')}:</strong> {invoice.bankAccount}
            </p>
            <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
              <strong>{t('paymentConfirmation.referenceNumber')}:</strong> {invoice.invoiceNumber}
            </p>
            <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
              <strong>{t('paymentConfirmation.recipient')}:</strong> Lizzard Solutions 
            </p>
            <p style={{ margin: '0.5rem 0', fontSize: '14px', color: '#374151' }}>
              <strong>{t('invoiceCancel.amount')}:</strong> {invoice.amount} {invoice.currency}
            </p>
          </div>
        </div>

        {/* Payment Confirmation Form */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', marginBottom: '1.5rem' }}>
            {t('paymentConfirmation.title')}
          </h2>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              {t('paymentConfirmation.paymentProof')}
            </label>
            <textarea
              value={paymentProof}
              onChange={(e) => setPaymentProof(e.target.value)}
              placeholder={t('paymentConfirmation.paymentProofPlaceholder')}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => navigate('/invoice')}
              style={{
                padding: '8px 16px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {t('paymentConfirmation.cancel')}
            </button>
            <button
              onClick={handleConfirmPayment}
              disabled={submitting}
              style={{
                padding: '8px 16px',
                background: submitting ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? t('paymentConfirmation.submitting') : t('paymentConfirmation.confirmPayment')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 